import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import liff from '@line/liff';
import { useMockLiff, shouldUseMockLiff } from '../hooks/useMockLiff.js';
import { isSquarePath } from '../utils/getGroupIdFromUrl';

interface LiffProfile {
  userId: string;
  displayName: string;
  pictureUrl?: string;
}

interface LiffContextValue {
  isReady: boolean;
  profile: LiffProfile | null;
  error: Error | null;
  isLoggedIn: boolean;
  liff: typeof liff | null;
  shouldUseLiff: boolean;
}

const LiffContext = createContext<LiffContextValue | undefined>(undefined);

/**
 * LIFF Context Provider
 * 在 App 级别初始化 LIFF，所有子组件共享同一个 LIFF 实例和身份认证信息
 */
export function LiffProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const [isReady, setIsReady] = useState(false);
  const [profile, setProfile] = useState<LiffProfile | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  /** 僅在成功執行 liff.init() 後為 true；Square 等未 init 的路徑為 false，避免子層呼叫 getIDToken 等觸發 SDK 錯誤 */
  const [hasInitedLiff, setHasInitedLiff] = useState(false);

  /** 已針對哪個 LIFF ID 做過 init；路徑變更導致 LIFF ID 不同時會 re-init */
  const lastLiffIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  /** 未登入時是否已嘗試過自動 liff.login（僅執行一次，避免從 LINE 導回後重複 redirect）；re-init 時會重置 */
  const hasAttemptedAutoLoginRef = useRef(false);

  // 檢查是否應該使用 Mock LIFF
  const useMock = shouldUseMockLiff();

  // 除錯：記錄 Mock 模式狀態
  useEffect(() => {
    console.log('[LiffContext] Mock LIFF 檢查:', {
      useMock,
      mode: import.meta.env.MODE,
      dev: import.meta.env.DEV,
      viteEnableLiffMock: import.meta.env.VITE_ENABLE_LIFF_MOCK,
    });
  }, [useMock]);

  // 總是調用 useMockLiff（React Hooks 規則要求無條件調用）
  // 但只在 useMock 為 true 時使用其返回值
  const mockLiffResult = useMockLiff();
  const mockLiff = useMock ? mockLiffResult : null;

  // 除錯：記錄 mockLiff 的 isReady 狀態變化
  useEffect(() => {
    if (useMock && mockLiff) {
      console.log('[LiffContext] Mock LIFF isReady 狀態:', {
        mockLiffIsReady: mockLiff.isReady,
        contextIsReady: isReady,
        shouldSync: mockLiff.isReady !== isReady,
      });
    }
    // mockLiff 為穩定參考，不納入 deps 避免多餘同步
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMock, mockLiff?.isReady, isReady]);

  // 除錯：記錄 Mock LIFF 結果
  useEffect(() => {
    if (useMock) {
      console.log('[LiffContext] Mock LIFF 結果:', {
        hasMockLiff: !!mockLiff,
        isReady: mockLiff?.isReady,
        isLoggedIn: mockLiff?.isLoggedIn,
        hasLiff: !!mockLiff?.liff,
      });
    }
  }, [useMock, mockLiff]);

  // 檢查是否應該啟用 LIFF：
  // - Mock 模式一律啟用
  // - 若設定 VITE_LIFF_FORCE_OFF='true' 則完全關閉（保險絲，用於 dev/staging）
  // - 否則在任一路徑有 LIFF ID 設定時啟用
  const shouldUseLiff = (() => {
    if (useMock) return true;
    if (import.meta.env.VITE_LIFF_FORCE_OFF === 'true') return false;
    const hasAnyLiffId = !!(
      import.meta.env.VITE_LIFF_ID ||
      import.meta.env.VITE_LIFF_ID_SURVEY ||
      import.meta.env.VITE_LIFF_ID_MEMBER ||
      import.meta.env.VITE_LIFF_ID_PREORDER ||
      import.meta.env.VITE_LIFF_ID_PICKUP ||
      import.meta.env.VITE_LIFF_ID_JOIN ||
      import.meta.env.VITE_LIFF_ID_BOT ||
      import.meta.env.VITE_LIFF_ID_ONLINE_SHOPPING ||
      import.meta.env.VITE_LIFF_ID_ONLINE_ORDER
    );
    return hasAnyLiffId;
  })();

  // 根據當前路徑取得對應的 LIFF ID
  const getLiffIdByPath = (): string | undefined => {
    const pathname = location.pathname;

    // 根據路徑選擇對應的 LIFF ID
    let liffId: string | undefined;

    if (pathname.startsWith('/survey')) {
      liffId = import.meta.env.VITE_LIFF_ID_SURVEY;
    } else if (pathname.startsWith('/member')) {
      liffId = import.meta.env.VITE_LIFF_ID_MEMBER;
    } else if (pathname.startsWith('/preorder')) {
      liffId = import.meta.env.VITE_LIFF_ID_PREORDER;
    } else if (pathname.startsWith('/online-shopping')) {
      liffId = import.meta.env.VITE_LIFF_ID_ONLINE_SHOPPING;
    } else if (pathname.startsWith('/online-order')) {
      liffId = import.meta.env.VITE_LIFF_ID_ONLINE_ORDER;
    } else if (pathname.startsWith('/pickup')) {
      liffId = import.meta.env.VITE_LIFF_ID_PICKUP;
    } else if (
      pathname.startsWith('/lite/join') ||
      (pathname.startsWith('/lite/shop/') && pathname.includes('/join'))
    ) {
      // Join 頁（/lite/join 或 /lite/shop/:suffix/join/:activityId）只用後端 LINE Login，不 init LIFF，
      // 避免 liff.login() 把前端 URL 當 redirect_uri 送給 LINE（channel 2008752206）導致 400 invalid url
      liffId = undefined;
    } else if (pathname.startsWith('/lite')) {
      // 其他 Lite 功能（含 Square 流程）使用 Bot Webhook 專屬的 LIFF ID 或通用的 LIFF ID
      liffId = import.meta.env.VITE_LIFF_ID_BOT || import.meta.env.VITE_LIFF_ID;
    }

    // 如果沒有找到專屬的 LIFF ID，使用通用的 LIFF ID 作為後備
    if (!liffId || liffId === 'undefined' || liffId.trim() === '') {
      liffId = import.meta.env.VITE_LIFF_ID;
    }

    if (!liffId || liffId === 'undefined' || liffId.trim() === '') {
      return undefined;
    }

    return liffId.trim();
  };

  /** 是否為有效 LIFF ID（非空、非字串 "undefined"、格式含連字號） */
  const isValidLiffId = (id: string | undefined): id is string =>
    typeof id === 'string' &&
    id !== 'undefined' &&
    id.trim() !== '' &&
    id.trim().includes('-');

  // 初始化 LIFF 的函數（僅在 liffId 有效時呼叫 SDK，避免 "liffId is necessary for liff.init()"）
  const initializeLiff = (liffId: string) => {
    const trimmed = typeof liffId === 'string' ? liffId.trim() : '';
    if (!trimmed || liffId === 'undefined') {
      console.error('❌ LIFF ID 不能為空或為字串 "undefined"', {
        pathname: location.pathname,
      });
      setError(new Error('LIFF ID 未設定或無效'));
      setIsReady(true);
      return;
    }
    if (!trimmed.includes('-')) {
      console.warn('❌ LIFF ID 格式無效（應含連字號），不呼叫 liff.init', {
        pathname: location.pathname,
      });
      setError(new Error('LIFF ID 格式無效，請檢查環境變數'));
      setIsReady(true);
      return;
    }

    console.log('🔄 初始化 LIFF...', {
      liffId: trimmed,
      pathname: location.pathname,
    });

    // 逾時：若 LIFF 一直未 resolve/reject（例如在外部瀏覽器開啟），8 秒後強制 isReady 讓頁面可顯示
    const LIFF_INIT_TIMEOUT_MS = 8000;
    let resolved = false;
    const timeoutId = window.setTimeout(() => {
      if (resolved || !isMountedRef.current) return;
      resolved = true;
      console.warn('⚠️ LIFF 初始化逾時，可能不在 LINE 內開啟', {
        liffId: trimmed,
        pathname: location.pathname,
      });
      setIsReady(true);
      setIsLoggedIn(false);
    }, LIFF_INIT_TIMEOUT_MS);

    const clearTimeoutAndMarkResolved = () => {
      if (!resolved) {
        resolved = true;
        window.clearTimeout(timeoutId);
      }
    };

    liff
      .init({ liffId: trimmed })
      .then(() => {
        clearTimeoutAndMarkResolved();
        if (!isMountedRef.current) return;

        console.log('✅ LIFF 初始化成功', {
          liffId: trimmed,
          pathname: location.pathname,
        });
        setIsReady(true);
        setHasInitedLiff(true);

        const loggedIn = liff.isLoggedIn();
        setIsLoggedIn(loggedIn);

        if (loggedIn) {
          console.log('✅ 使用者已登入，取得個人資料...');
          return liff.getProfile();
        }
        // 未登入時僅執行一次自動登入（外部瀏覽器支援）；避免從 LINE 導回後重複 redirect
        if (!hasAttemptedAutoLoginRef.current) {
          hasAttemptedAutoLoginRef.current = true;
          const redirectUri =
            window.location.origin +
            window.location.pathname +
            window.location.search;
          liff.login({ redirectUri });
          return null;
        }
        return null;
      })
      .then(userProfile => {
        if (!isMountedRef.current) return;

        if (userProfile) {
          console.log('✅ 取得使用者資料:', userProfile);
          setProfile({
            userId: userProfile.userId,
            displayName: userProfile.displayName,
            pictureUrl: userProfile.pictureUrl,
          });
        }
      })
      .catch(err => {
        clearTimeoutAndMarkResolved();
        if (!isMountedRef.current) return;

        const errorCode = (err as { code?: string })?.code;
        const errorMessage = err instanceof Error ? err.message : String(err);

        console.error('❌ LIFF 初始化失敗:', err, {
          liffId: trimmed,
          pathname: location.pathname,
          errorMessage,
          errorCode,
        });

        // 如果是 INVALID_CONFIG 錯誤，可能是 LIFF ID 設定錯誤
        // 但 LINE 可能已經在 URL hash 中提供了 token，嘗試繼續
        if (errorCode === 'INVALID_CONFIG') {
          console.warn(
            '⚠️ LIFF ID 設定可能錯誤，但嘗試繼續（LINE 可能已在 URL 中提供 token）'
          );
          // 不設定錯誤，讓頁面繼續運作，嘗試從 URL hash 取得 token
          setIsReady(true);
          setIsLoggedIn(false); // 標記為未登入，讓頁面嘗試其他方式取得 token
        } else {
          let finalErrorMessage = 'LIFF 初始化失敗';
          if (err instanceof Error) {
            finalErrorMessage = err.message;
          } else if (typeof err === 'string') {
            finalErrorMessage = err;
          } else if (err && typeof err === 'object' && 'message' in err) {
            finalErrorMessage = String(err.message);
          }

          setError(new Error(finalErrorMessage));
          setIsReady(true);
        }
      });
  };

  // 如果使用 Mock 模式，同步 Mock LIFF 的狀態並暴露到 window
  useEffect(() => {
    // React Strict Mode 會在開發環境下對元件進行「mount → unmount → 再 mount 一次」的雙重掛載。
    // 先前我們在 cleanup 將 isMountedRef 設為 false，第二次真正 mount 時 useRef 不會重置，導致後續 async then/catch 中
    // 的 `if (!isMountedRef.current) return;` 直接提前返回，永遠不會執行 setIsReady(true) 等狀態更新，畫面就會卡在「載入中」。
    // 因此每次 effect 執行時都先將 isMountedRef.current 重設為 true，確保第二次 mount 時 async callback 仍會生效。
    isMountedRef.current = true;

    if (useMock) {
      if (!mockLiff) {
        console.warn(
          '[LiffContext] ⚠️ Mock 模式啟用但 mockLiff 為 null，等待初始化...'
        );
        // 即使 mockLiff 為 null，也設置 isReady 為 true，避免卡在載入中
        setIsReady(true);
        setIsLoggedIn(false);
        return;
      }

      console.log('[LiffContext] 🔄 開始初始化 Mock LIFF 狀態', {
        isReady: mockLiff.isReady,
        isLoggedIn: mockLiff.isLoggedIn,
        hasLiff: !!mockLiff.liff,
        hasProfile: !!mockLiff.profile,
      });

      // Mock LIFF 已經在 useMockLiff hook 中初始化
      // 同步 Mock LIFF 的狀態到 Context（需要監聽狀態變化）
      const updateState = () => {
        if (mockLiff) {
          const newIsReady = mockLiff.isReady;
          const newIsLoggedIn = mockLiff.isLoggedIn;
          const newProfile = mockLiff.profile;

          // 強制更新狀態，確保 React 知道狀態已改變
          setIsReady(prev => {
            if (prev !== newIsReady) {
              console.log(
                '[LiffContext] 🔄 isReady 狀態更新:',
                prev,
                '->',
                newIsReady
              );
            }
            return newIsReady;
          });

          setIsLoggedIn(prev => {
            if (prev !== newIsLoggedIn) {
              console.log(
                '[LiffContext] 🔄 isLoggedIn 狀態更新:',
                prev,
                '->',
                newIsLoggedIn
              );
            }
            return newIsLoggedIn;
          });

          setProfile(newProfile);
          setError(mockLiff.error);
        }
      };

      // 立即更新一次
      updateState();

      // 如果 mockLiff.isReady 已經是 true，立即同步（避免等待輪詢）
      // 使用 setTimeout 確保在狀態更新後執行
      if (mockLiff.isReady) {
        setTimeout(() => {
          console.log('[LiffContext] ⚡ Mock LIFF 已就緒，立即同步狀態');
          setIsReady(true);
          setIsLoggedIn(mockLiff.isLoggedIn);
          setProfile(mockLiff.profile);
        }, 0);
      }

      // 監聽 Mock LIFF 狀態變化（使用輪詢，因為 useMockLiff 沒有提供事件）
      // 增加輪詢頻率，確保狀態及時更新
      const interval = setInterval(updateState, 50);

      // 將 Mock LIFF 暴露到 window 物件，方便除錯
      if (typeof window !== 'undefined' && mockLiff.liff) {
        (window as Window & { liff?: unknown }).liff = mockLiff.liff;
        console.log('[LiffContext] ✅ Mock LIFF 已暴露到 window.liff', {
          hasGetContext: typeof mockLiff.liff.getContext === 'function',
          hasGetIDToken: typeof mockLiff.liff.getIDToken === 'function',
          isReady: mockLiff.isReady,
          isLoggedIn: mockLiff.isLoggedIn,
        });
      } else {
        console.error('[LiffContext] ❌ 無法暴露 Mock LIFF 到 window.liff', {
          hasWindow: typeof window !== 'undefined',
          hasLiff: !!mockLiff.liff,
          mockLiffKeys: mockLiff ? Object.keys(mockLiff) : null,
        });
      }

      return () => {
        clearInterval(interval);
      };
    }

    // 如果不需要 LIFF，直接返回
    if (!shouldUseLiff) {
      setIsReady(true);
      return;
    }

    // 共用模組化改善：依「當前路徑」取得 LIFF ID；路徑變更導致 LIFF ID 不同時 re-init
    const envLiffId = getLiffIdByPath();

    if (lastLiffIdRef.current === envLiffId && envLiffId != null) {
      // 已針對此 LIFF ID 做過 init，無需重複
      return;
    }

    // 路徑變更時先視為未 init，僅在 init 成功後才暴露 liff 給子層（避免 Square 等路徑呼叫 getIDToken 觸發 "liffId is necessary for liff.init()"）
    setHasInitedLiff(false);

    // 新路徑或 LIFF ID 變更：重置狀態並 re-init
    lastLiffIdRef.current = envLiffId ?? null;
    hasAttemptedAutoLoginRef.current = false;
    setIsReady(false);
    setError(null);
    setIsLoggedIn(false);
    setProfile(null);

    if (envLiffId && isValidLiffId(envLiffId)) {
      const source = location.pathname.startsWith('/survey')
        ? 'SURVEY'
        : location.pathname.startsWith('/member')
          ? 'MEMBER'
          : location.pathname.startsWith('/preorder')
            ? 'PREORDER'
            : location.pathname.startsWith('/lite')
              ? 'LITE'
              : 'UNIFIED';

      console.log('📦 使用環境變數中的 LIFF ID:', {
        liffId: envLiffId,
        pathname: location.pathname,
        source,
      });

      initializeLiff(envLiffId);
    } else {
      // envLiffId 為 undefined，或為無效值（如字串 "undefined"、空、缺連字號）
      const isSquareRoute =
        location.pathname === '/lite/new-square' ||
        location.pathname === '/lite/oauth-return' ||
        location.pathname === '/lite/square-choice' ||
        location.pathname === '/lite/square-create' ||
        location.pathname === '/lite/square-enter' ||
        isSquarePath(location.pathname);
      const isRootOrNonLiffPath = location.pathname === '/';
      if (isSquareRoute) {
        console.log('[LiffContext] Square 路由不啟用 LIFF:', location.pathname);
      } else if (isRootOrNonLiffPath) {
        console.log('[LiffContext] 根路徑 / 不需 LIFF，跳過 init');
      } else if (envLiffId !== undefined && envLiffId !== '') {
        console.error('❌ LIFF ID 無效（未設定或格式錯誤），不呼叫 liff.init', {
          pathname: location.pathname,
        });
        setError(
          new Error('LIFF ID 未設定或格式無效，請檢查 VITE_LIFF_ID_* 環境變數')
        );
      } else {
        console.error('❌ 環境變數 LIFF ID 未設定', {
          pathname: location.pathname,
        });
        setError(
          new Error(
            `LIFF ID 未設定，請檢查環境變數（路徑: ${location.pathname}）`
          )
        );
      }
      setIsReady(true);
    }

    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    shouldUseLiff,
    useMock,
    mockLiff?.isReady,
    mockLiff?.isLoggedIn,
    mockLiff?.profile,
    location.pathname, // 路徑變更時重取 LIFF ID 並在必要時 re-init
  ]);

  // 僅在 Mock 或「已成功 init LIFF」時暴露 liff；Square 等未 init 路徑暴露 null，避免 getIDToken 等觸發 SDK 錯誤
  const liffInstance =
    useMock && mockLiff
      ? (mockLiff.liff as unknown as typeof liff)
      : shouldUseLiff && hasInitedLiff
        ? liff
        : null;

  // 將 liff 實例暴露到 window 物件，方便除錯（僅在開發環境）
  useEffect(() => {
    if (typeof window !== 'undefined' && liffInstance) {
      (window as Window & { liff?: unknown }).liff = liffInstance;
      if (useMock) {
        console.log('[LiffContext] ✅ Mock LIFF 已暴露到 window.liff', {
          hasGetContext: typeof liffInstance.getContext === 'function',
          hasGetIDToken: typeof liffInstance.getIDToken === 'function',
        });
      }
    } else if (typeof window !== 'undefined' && useMock) {
      console.warn('[LiffContext] ⚠️ Mock 模式但 liffInstance 為 null', {
        useMock,
        hasMockLiff: !!mockLiff,
        shouldUseLiff,
      });
    }
  }, [liffInstance, useMock, mockLiff, shouldUseLiff]);

  const value: LiffContextValue = {
    isReady,
    profile,
    error,
    isLoggedIn,
    liff: liffInstance,
    shouldUseLiff,
  };

  return <LiffContext.Provider value={value}>{children}</LiffContext.Provider>;
}

/**
 * Hook to use LIFF context
 * 替代原来的 useLiff hook，从 Context 获取 LIFF 状态
 */
export function useLiff(): LiffContextValue {
  const context = useContext(LiffContext);
  if (context === undefined) {
    throw new Error('useLiff must be used within a LiffProvider');
  }
  return context;
}
